import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  TrendingUp,
  Clock,
  Award,
  PlayCircle,
  Loader2
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStats, setUserStats] = useState({
    coursesInProgress: 0,
    hoursLearned: 0,
    certifications: 0,
    completionRate: 0,
    recentProgress: {
      coursesStarted: 0,
      hoursThisWeek: 0,
      completionIncrease: 0
    }
  });
  const [userName, setUserName] = useState('');
  const [inProgressCourses, setInProgressCourses] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error('No authenticated user found');
        }

        // Fetch user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name')
          .eq('id', user.id)
          .single();

        setUserName(profile?.first_name || 'User');

        // Fetch enrollments with course and lesson details
        const { data: enrollments } = await supabase
          .from('enrollments')
          .select(`
            *,
            course:courses(
              id,
              title,
              thumbnail_url,
              lessons(
                id,
                duration
              )
            ),
            lesson_progress(
              lesson_id,
              completed,
              completed_at
            )
          `)
          .eq('user_id', user.id);

        // Calculate statistics
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const stats = enrollments.reduce((acc, enrollment) => {
          const lessons = enrollment.course?.lessons || [];
          const lessonsProgress = enrollment.lesson_progress || [];
          const completedLessons = lessonsProgress.filter(lp => lp.completed);
          const progress = lessons.length > 0 
            ? (completedLessons.length / lessons.length) * 100 
            : 0;

          // Calculate hours learned from completed lessons duration
          const hoursLearned = completedLessons.reduce((sum, lp) => {
            const lesson = lessons.find(l => l.id === lp.lesson_id);
            if (!lesson?.duration) return sum;
            const [hours, minutes] = lesson.duration.split(':').map(Number);
            return sum + (hours + minutes / 60);
          }, 0);

          // Calculate hours learned this week
          const hoursThisWeek = completedLessons
            .filter(lp => new Date(lp.completed_at) > weekAgo)
            .reduce((sum, lp) => {
              const lesson = lessons.find(l => l.id === lp.lesson_id);
              if (!lesson?.duration) return sum;
              const [hours, minutes] = lesson.duration.split(':').map(Number);
              return sum + (hours + minutes / 60);
            }, 0);

          // Update totals
          if (progress < 100) acc.coursesInProgress++;
          if (progress === 100) acc.certifications++;
          acc.hoursLearned += hoursLearned;
          acc.recentProgress.hoursThisWeek += hoursThisWeek;
          
          return acc;
        }, {
          coursesInProgress: 0,
          hoursLearned: 0,
          certifications: 0,
          completionRate: 0,
          recentProgress: {
            coursesStarted: 0,
            hoursThisWeek: 0,
            completionIncrease: 0
          }
        });

        // Calculate completion rate
        stats.completionRate = enrollments.length > 0
          ? (stats.certifications / enrollments.length) * 100
          : 0;

        // Calculate completion increase (compare with previous week)
        const previousCompletionRate = 0; // TODO: Fetch historical data
        stats.recentProgress.completionIncrease = Math.max(0, stats.completionRate - previousCompletionRate);

        setUserStats(stats);

        // Prepare in-progress courses data
        const inProgress = enrollments
          .map(enrollment => {
            const lessons = enrollment.course?.lessons || [];
            const lessonsProgress = enrollment.lesson_progress || [];
            const completedLessons = lessonsProgress.filter(lp => lp.completed).length;
            const progress = lessons.length > 0 
              ? (completedLessons / lessons.length) * 100 
              : 0;

            if (progress >= 100) return null;

            return {
              id: enrollment.course.id,
              title: enrollment.course.title,
              progress,
              thumbnail: enrollment.course.thumbnail_url || "/api/placeholder/400/225",
              completedLessons,
              totalLessons: lessons.length,
              lastAccessed: formatLastAccessed(enrollment.last_accessed)
            };
          })
          .filter(Boolean)
          .sort((a, b) => b.progress - a.progress)
          .slice(0, 3);

        setInProgressCourses(inProgress);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatLastAccessed = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const lastAccess = new Date(date);
    const diffDays = Math.floor((now - lastAccess) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

  const statsCards = [
    {
      title: 'Courses in Progress',
      value: userStats.coursesInProgress,
      icon: BookOpen,
      trend: `+${userStats.recentProgress.coursesStarted} this week`
    },
    {
      title: 'Hours Learned',
      value: userStats.hoursLearned.toFixed(1),
      icon: Clock,
      trend: `+${userStats.recentProgress.hoursThisWeek.toFixed(1)} hrs this week`
    },
    {
      title: 'Certifications',
      value: userStats.certifications,
      icon: Award
    },
    {
      title: 'Course Completion',
      value: `${Math.round(userStats.completionRate)}%`,
      icon: TrendingUp,
      trend: userStats.recentProgress.completionIncrease > 0
        ? `+${userStats.recentProgress.completionIncrease.toFixed(1)}% increase`
        : null
    }
  ];

  const CourseProgressCard = ({ course }) => (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
      <div className="flex items-center p-4">
        <img 
          src={course.thumbnail}
          alt={course.title}
          className="w-24 h-16 object-cover rounded-lg"
        />
        <div className="ml-4 flex-1">
          <h3 className="font-medium text-gray-900 mb-1">{course.title}</h3>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              {course.completedLessons}/{course.totalLessons} Lessons
            </span>
            <span className="text-sm text-gray-600">{course.lastAccessed}</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-900 rounded-full transition-all"
              style={{ width: `${course.progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, {userName}! 👋</h1>
          <p className="text-gray-600 mt-1">Continue your learning journey</p>
        </div>
        <button 
          onClick={() => window.location.href = '/courses'}
          className="flex items-center gap-2 bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
        >
          <BookOpen size={20} />
          <span>Start New Course</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsCards.map((card, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm mb-1">{card.title}</p>
                <h3 className="text-2xl font-bold text-gray-900">{card.value}</h3>
                {card.trend && (
                  <p className="text-green-500 text-sm mt-2 flex items-center gap-1">
                    <TrendingUp size={16} />
                    {card.trend}
                  </p>
                )}
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <card.icon className="text-blue-900" size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Continue Learning Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Continue Learning</h2>
          <button 
            onClick={() => window.location.href = '/my-courses'}
            className="text-blue-900 hover:text-blue-800"
          >
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {inProgressCourses.map(course => (
            <CourseProgressCard key={course.id} course={course} />
          ))}
          {inProgressCourses.length === 0 && (
            <div className="text-center py-8 bg-white rounded-xl">
              <BookOpen size={48} className="mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">No courses in progress</p>
              <button 
                onClick={() => window.location.href = '/courses'}
                className="mt-4 text-blue-900 hover:text-blue-800"
              >
                Browse Courses
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;